/**
 * =============================================================================
 * Hook: useEntityVisibility
 * Versão: 1.0.0 (2026-06-25)
 *
 * Descrição:
 *   Wrapper das RPCs unificadas `get_entity_visibility` e `set_entity_visibility`
 *   que controlam a visibilidade (privada / compartilhada / por perfil / todos)
 *   das entidades: Listas de Campanha, Campanhas e Templates de WhatsApp.
 *
 *   Reaproveita o conceito já implementado nas Ações Rápidas, sem precisar
 *   alterar as funções de criação existentes — o frontend chama estas RPCs
 *   após criar/editar o registro principal.
 * =============================================================================
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { showToast } from '@/lib/toast';
import { createLogger } from '@/lib/logger';

const log = createLogger('useEntityVisibility');

/** Modos suportados (espelham o enum entity_visibility no banco). */
export type EntityVisibility = 'private' | 'shared' | 'by_profile' | 'public';

/** Tipos de entidade aceitos pela RPC. */
export type VisibilityEntityType =
  | 'campaign_list'
  | 'campaign'
  | 'whatsapp_template'
  | 'task_checklist_template';

/** Configuração de visibilidade retornada pela RPC `get_entity_visibility`. */
export interface EntityVisibilityConfig {
  visibility: EntityVisibility;
  allowed_user_ids: string[];
  allowed_profile_ids: string[];
}

interface UseEntityVisibilityArgs {
  entityType: VisibilityEntityType;
  entityId: string | null | undefined;
  /** Habilita/desabilita a query (default TRUE quando há id). */
  enabled?: boolean;
}

/**
 * Carrega a configuração atual de visibilidade de um registro específico.
 */
export function useEntityVisibility({ entityType, entityId, enabled = true }: UseEntityVisibilityArgs) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: ['entity-visibility', entityType, entityId, tenantId],
    enabled: enabled && !!entityId && !!tenantId,
    staleTime: 30_000,
    queryFn: async (): Promise<EntityVisibilityConfig> => {
      const { data, error } = await (supabase.rpc as any)('get_entity_visibility', {
        p_entity_type: entityType,
        p_entity_id: entityId,
        p_tenant_hint: tenantId ?? null,
      });
      if (error) {
        log.error('Falha ao carregar visibilidade', { entityType, entityId, error: error.message });
        throw error;
      }
      const cfg = (data || {}) as Partial<EntityVisibilityConfig>;
      return {
        visibility: (cfg.visibility ?? 'public') as EntityVisibility,
        allowed_user_ids: cfg.allowed_user_ids ?? [],
        allowed_profile_ids: cfg.allowed_profile_ids ?? [],
      };
    },
  });
}

interface SetEntityVisibilityInput {
  entityType: VisibilityEntityType;
  entityId: string;
  visibility: EntityVisibility;
  allowedUserIds?: string[];
  allowedProfileIds?: string[];
}

/**
 * Salva a configuração de visibilidade. Invalida queries relacionadas
 * (a própria visibilidade + listagens conhecidas) para refletir na UI.
 */
export function useSetEntityVisibility() {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: SetEntityVisibilityInput) => {
      const { data, error } = await (supabase.rpc as any)('set_entity_visibility', {
        p_entity_type: input.entityType,
        p_entity_id: input.entityId,
        p_visibility: input.visibility,
        p_allowed_user_ids:
          input.visibility === 'shared' ? (input.allowedUserIds ?? []) : null,
        p_allowed_profile_ids:
          input.visibility === 'by_profile' ? (input.allowedProfileIds ?? []) : null,
        p_tenant_hint: currentTenant?.id ?? null,
      });
      if (error) {
        log.error('Falha ao salvar visibilidade', { ...input, error: error.message });
        throw error;
      }
      return data as { ok: boolean };
    },
    onSuccess: (_data, input) => {
      // Invalida cache da própria visibilidade
      queryClient.invalidateQueries({
        queryKey: ['entity-visibility', input.entityType, input.entityId],
      });
      // Invalida listagens conhecidas que mudam por causa da visibilidade
      if (input.entityType === 'campaign_list') {
        queryClient.invalidateQueries({ queryKey: ['campaign-lists'] });
      } else if (input.entityType === 'campaign') {
        queryClient.invalidateQueries({ queryKey: ['campaigns'] });
        queryClient.invalidateQueries({ queryKey: ['tenant-campaigns-list'] });
      } else if (input.entityType === 'whatsapp_template') {
        queryClient.invalidateQueries({ queryKey: ['whatsapp-templates'] });
      } else if (input.entityType === 'task_checklist_template') {
        queryClient.invalidateQueries({ queryKey: ['task-checklist-templates'] });
      }
      showToast.success('Visibilidade atualizada');
    },
    onError: (err: Error) => {
      showToast.error('Erro ao atualizar visibilidade', err.message);
    },
  });
}
